import { useMemo } from 'react';
import { Navigate } from 'react-router-dom';

import { CalendarIcon } from '@/components/Icons';
import { StatMetric } from '@/components/StatMetric';
import { useSessionState } from '@/context/SessionContext';
import { useHomeDashboard } from '@/hooks/useHomeDashboard';
import { formatCompactNumber, formatInrCurrency } from '@/lib/format';

const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

function formatDensity(value: number | undefined) {
  if (!value) {
    return '';
  }

  return value > 1 ? `${value} bookings` : '1 booking';
}

export function CalendarPage() {
  const { isReady: sessionReady, isEnterprise } = useSessionState();
  const { data: homeData, isLoading, isFromApi, error, refresh } = useHomeDashboard();

  const calendar = homeData.calendar;
  const bookingsByDay = calendar.booking_density;
  const todayAppointments = useMemo(() => [...(homeData.today_appointments ?? [])], [homeData.today_appointments]);

  const busiestDay = useMemo(() => {
    const peakEntry = Object.entries(bookingsByDay).sort((left, right) => right[1] - left[1])[0];

    if (!peakEntry) {
      return 'No bookings yet';
    }

    return `Day ${peakEntry[0]} · ${formatDensity(peakEntry[1])}`;
  }, [bookingsByDay]);

  const busyDayCount = useMemo(
    () => Object.values(bookingsByDay).filter((count) => count > 0).length,
    [bookingsByDay]
  );

  if (!sessionReady || isLoading) {
    return <div className="section">Loading calendar...</div>;
  }

  if (!isEnterprise) {
    return <Navigate to="/attendance" replace />;
  }

  return (
    <div className="section page-grid calendar-page">
      <section className="hero-layout">
        <div className="hero-copy">
          <span className="eyebrow">
            <CalendarIcon size={14} />
            <strong>Calendar</strong>
            <span>{isFromApi ? 'Live API' : homeData.month_label}</span>
          </span>

          <h1 className="display-title">
            <span>Booking calendar</span>
          </h1>

          {error ? (
            <div className="notice error">
              <span>{error}</span>
              <button className="ghost-button" type="button" onClick={refresh}>
                Retry
              </button>
            </div>
          ) : null}
        </div>

        <div className="stats-grid">
          <StatMetric
            label="Bookings"
            value={formatCompactNumber(homeData.metrics.weekly_bookings)}
            hint="This week's bookings"
          />
          <StatMetric
            label="Revenue"
            value={formatInrCurrency(homeData.metrics.weekly_revenue)}
            hint="Weekly revenue"
            highlight
          />
          <StatMetric label="Retention" value={`${homeData.metrics.retention}%`} hint="Repeat clients" />
          <StatMetric
            label="Rating"
            value={homeData.metrics.rating.toFixed(1)}
            hint="Customer feedback score"
          />
        </div>
      </section>

      <section className="calendar-layout">
        <div className="stack">
          <div className="panel calendar-panel">
            <div className="panel-title-row">
              <div>
                <h2 className="panel-title">Month view</h2>
                <p className="panel-caption">{homeData.today_label}</p>
              </div>
              <div className="badge soft">{busyDayCount} busy days</div>
            </div>

            <div className="calendar-grid-head" aria-hidden="true">
              {WEEK_DAYS.map((day) => (
                <span key={day}>{day}</span>
              ))}
            </div>

            <div className="calendar-grid" role="grid" aria-label="Booking calendar">
              {calendar.cells.map((day, index) => {
                if (day === null) {
                  return <div key={`empty-${index}`} className="calendar-cell empty" aria-hidden="true" />;
                }

                const density = bookingsByDay[String(day)] ?? 0;
                const isHighlighted = calendar.highlighted_day === day;

                return (
                  <div key={day} className={`calendar-cell${isHighlighted ? ' highlighted' : ''}`}>
                    <span className="calendar-day">{day}</span>
                    {density > 0 ? (
                      <span className="calendar-density">{density}</span>
                    ) : (
                      <span className="calendar-density muted">•</span>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="calendar-summary">
              <div className="calendar-summary-item">
                <span>Peak day</span>
                <strong>{busiestDay}</strong>
              </div>
              <div className="calendar-summary-item">
                <span>Today</span>
                <strong>{homeData.today_label}</strong>
              </div>
              <div className="calendar-summary-item">
                <span>Appointments</span>
                <strong>{todayAppointments.length}</strong>
              </div>
            </div>
          </div>
        </div>

        <div className="stack">
          <div className="panel">
            <div className="section-heading">
              <div>
                <h2>Today&apos;s appointments</h2>
              </div>
            </div>

            <div className="list">
              {todayAppointments.length > 0 ? (
                todayAppointments.map((appointment) => (
                  <div className="list-item" key={appointment.id}>
                    <div>
                      <strong>{appointment.client_name}</strong>
                      <span>
                        {appointment.time} · {appointment.staff_name}
                      </span>
                      <p>{appointment.services.join(' · ')}</p>
                    </div>
                    <div className="item-meta">
                      <span className="badge soft bottom-stack-sm">{appointment.status.replace('_', ' ')}</span>
                      <strong>{formatInrCurrency(appointment.total_amount)}</strong>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state">No appointments are scheduled for today.</div>
              )}
            </div>
          </div>

          <div className="panel">
            <div className="section-heading">
              <div>
                <h2>Team load</h2>
                <p>Staff performance from the same home payload.</p>
              </div>
            </div>

            <div className="list">
              {homeData.staff_performance.slice(0, 5).map((staff) => (
                <div className="list-item" key={staff.id}>
                  <div>
                    <strong>{staff.name}</strong>
                    <span>{staff.role}</span>
                    <p>
                      {staff.bookings} bookings · {staff.utilization}% utilization
                    </p>
                  </div>
                  <strong>{formatInrCurrency(staff.revenue)}</strong>
                </div>
              ))}
              {homeData.staff_performance.length === 0 ? (
                <div className="empty-state">No staff performance data yet.</div>
              ) : null}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
